import type { ReactElement } from 'react';
import {
  Children,
  Fragment,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useState,
} from 'react';

import type { FloatComponent } from './types';

import Motion, { m } from '@snowit/ui/utils/Motion';

import useDomElement from '../useDomElement';
import { fade, transition } from './animations';
import useFloat from './useFloat';

import { AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

const useFloatComponent = () => {
  const [mounted, setMounted] = useState(false);
  const reactID = useId();

  const mount = () => setMounted(true);
  const unmount = () => setMounted(false);

  const Float = (props: FloatComponent) => {
    const { id, children, strategy = 'click', content, onClick, animation = {}, offset } = props;

    const { enter = 'bottom', exit = 'bottom', value: animationValue } = animation;

    const [uid] = useState<string>(id || reactID);
    const { floating, parent, x, y } = useFloat({ offset, strategy });
    const element = useDomElement({ id: uid, prefix: 'float' });

    const handleOutside = (event) => {
      if (!mounted) return;
      for (const target of event.composedPath()) {
        if (target.id === uid) return;
      }
      setMounted(false);
    };

    useEffect(() => {
      if (!mounted) return () => {};
      setTimeout(() => {
        window.addEventListener('click', handleOutside);
      }, 100);
      return () => {
        window.removeEventListener('click', handleOutside);
      };
    }, [mounted]);

    const handleClick = () => {
      if (typeof onClick === 'function') {
        onClick();
      }
      setMounted((prev) => !prev);
    };

    const getProps = () => {
      return {
        ref: parent,
        ...(strategy === 'click' && {
          onClick: handleClick,
        }),
        ...(strategy === 'hover' && {
          onMouseEnter: () => setMounted(true),
          onMouseLeave: () => setMounted(false),
        }),
      };
    };

    const childrenWithProps = Children.map(children, (child) => {
      if (isValidElement(child)) {
        return cloneElement(child, getProps());
      }
      return child;
    });

    const Content = (
      <Motion>
        <AnimatePresence>
          {mounted ? (
            <m.div
              ref={floating}
              transition={transition}
              {...fade(enter, exit, animationValue)}
              style={{
                position: 'fixed',
                top: y ?? undefined,
                left: x ?? undefined,
                zIndex: 99999,
              }}
            >
              {typeof content === 'function' ? content() : content}
            </m.div>
          ) : null}
        </AnimatePresence>
      </Motion>
    );

    return (
      <Fragment>
        {childrenWithProps}
        {element && (createPortal(Content, element) as ReactElement)}
      </Fragment>
    );
  };

  return {
    mount,
    unmount,
    Float,
  };
};

export default useFloatComponent;
